class AutoplaySliderComponent extends HTMLElement {
  constructor() {
    super();

    // Le "track" = ton UL avec id="Slider-..."
    this.track = this.querySelector('[id^="Slider-"]');
    this.slides = this.track ? Array.from(this.track.querySelectorAll('.slider__slide')) : [];

    this.isPlaying = false;
    this.rafId = null;
    this.position = 0; // translateX en px
    this.trackWidth = 0;

    if (!this.track || this.slides.length === 0) return;

    // On neutralise le comportement slider Dawn pour CE track
    this.track.style.overflow = 'visible';
    this.track.style.scrollBehavior = 'auto';
    this.track.style.scrollSnapType = 'none';
    this.track.style.scrollPaddingLeft = '0';
    this.track.style.willChange = 'transform';

    // Setup infini
    this.setupInfiniteLoop();

    // Mesures
    this.initMeasurements();
    const resizeObserver = new ResizeObserver(() => {
      this.initMeasurements();
    });
    resizeObserver.observe(this.track);

    // Bouton autoplay
    this.sliderAutoplayButton = this.querySelector('.slideshow__autoplay');
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    if (this.track.getAttribute('data-autoplay') === 'true') {
      this.setAutoPlay();
    }
  }

  setupInfiniteLoop() {
    if (this.dataset.loopInitialized === 'true') return;
    this.dataset.loopInitialized = 'true';

    this.originalSlides = this.slides.slice();
    this.originalSlidesCount = this.originalSlides.length;

    if (this.originalSlidesCount < 2) return;

    const fragmentEnd = document.createDocumentFragment();

    this.originalSlides.forEach((item) => {
      const clone = item.cloneNode(true);
      clone.removeAttribute('id'); // pas de doublon d'id
      clone.setAttribute('data-clone', 'true');
      fragmentEnd.appendChild(clone);
    });

    this.track.appendChild(fragmentEnd);

    // Slides = originaux + clones
    this.slides = Array.from(this.track.querySelectorAll('.slider__slide'));

    // On force une disposition en ligne
    this.track.style.display = 'flex';
    this.track.style.flexWrap = 'nowrap';
  }

  initMeasurements() {
    if (!this.originalSlides || this.originalSlides.length === 0) return;

    const first = this.originalSlides[0];
    const last = this.originalSlides[this.originalSlides.length - 1];

    const firstRect = first.getBoundingClientRect();
    const lastRect = last.getBoundingClientRect();
    const trackRect = this.track.getBoundingClientRect();

    // largeur = (right du dernier - left du premier) dans le repère du track
    this.trackWidth = lastRect.right - firstRect.left;

    this.position = 0;
    this.applyTransform();
  }

  applyTransform() {
    this.track.style.transform = `translateX(${this.position}px)`;
  }

  /* ---------- AUTOPLAY CONTINU ---------- */

  setAutoPlay() {
    const speedSetting = this.track.dataset.speed || 'medium';

    const speedMap = {
      slow: 0.05,
      medium: 0.12,
      fast: 0.22,
    };

    this.pxPerMs = speedMap[speedSetting] || speedMap.medium;

    this.autoplayButtonIsSetToPlay = true;

    this.addEventListener('mouseover', this.focusInHandling.bind(this));
    this.addEventListener('mouseleave', this.focusOutHandling.bind(this));
    this.addEventListener('focusin', this.focusInHandling.bind(this));
    this.addEventListener('focusout', this.focusOutHandling.bind(this));

    if (this.sliderAutoplayButton) {
      this.sliderAutoplayButton.addEventListener('click', this.autoPlayToggle.bind(this));
      this.play();
    } else {
      this.reducedMotion.matches ? this.pause() : this.play();
    }
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusInHandling(event) {
    if (!this.sliderAutoplayButton) {
      this.pause();
      return;
    }

    const focusedOnAutoplayButton =
      event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);

    if (!focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
      this.pause();
    }
  }

  focusOutHandling(event) {
    if (!this.sliderAutoplayButton) {
      if (!this.reducedMotion.matches) this.play();
      return;
    }

    const focusedOnAutoplayButton =
      event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);

    if (this.autoplayButtonIsSetToPlay && !focusedOnAutoplayButton) {
      this.play();
    }
  }

  play() {
    if (this.isPlaying || !this.pxPerMs || !this.trackWidth) return;

    this.track.setAttribute('aria-live', 'off');
    this.isPlaying = true;
    this.lastFrameTime = performance.now();

    const step = (now) => {
      if (!this.isPlaying) return;

      const delta = now - this.lastFrameTime;
      this.lastFrameTime = now;

      this.continuousScroll(delta);

      this.rafId = requestAnimationFrame(step);
    };

    cancelAnimationFrame(this.rafId);
    this.rafId = requestAnimationFrame(step);
  }

  pause() {
    this.track.setAttribute('aria-live', 'polite');
    this.isPlaying = false;
    cancelAnimationFrame(this.rafId);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (!this.sliderAutoplayButton) return;

    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute(
        'aria-label',
        window.accessibilityStrings?.playSlideshow || 'Lire le diaporama'
      );
    } else {
      this.sliderAutoplayButton.classList.remove('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute(
        'aria-label',
        window.accessibilityStrings?.pauseSlideshow || 'Mettre le diaporama en pause'
      );
    }
  }

  continuousScroll(deltaMs) {
    if (!this.trackWidth) return;

    const distance = this.pxPerMs * deltaMs;
    this.position -= distance; // vers la gauche

    if (Math.abs(this.position) >= this.trackWidth) {
      this.position += this.trackWidth;
    }

    this.applyTransform();
  }
}

customElements.define('autoplay-slider-component', AutoplaySliderComponent);

class SpotlightSliderComponent extends SliderComponent {
  constructor() {
    super();

    // Radios sur l'image
    this.spotInputs = this.querySelectorAll('.spotlight-point-input');
    if (!this.slider || !this.spotInputs.length) return;

    // Créer un mapping entre valeur du spot et index réel du slide
    this.buildSpotToSlideMapping();

    // Quand on clique sur un spot -> aller au slide correspondant
    this.spotInputs.forEach((input) => {
      input.addEventListener('change', this.onSpotChange.bind(this));
    });

    // Quand le slider change (prev/next, swipe...) -> mettre à jour le spot sélectionné
    this.addEventListener('slideChanged', this.onSlideChanged.bind(this));
  }

  buildSpotToSlideMapping() {
    // Créer un objet qui map spotId -> slideIndex
    this.spotToSlideMap = {};

    // Parcourir les slides réels (non clonés) et récupérer leur data-spot-id
    const slides = this.slider.querySelectorAll('.spotlight__slide');

    slides.forEach((slide, index) => {
      // Le slide correspond au produit X, on récupère le spot associé
      // On peut utiliser l'ordre ou ajouter un data-attribute
      const spotInput = this.spotInputs[index];
      if (spotInput) {
        const spotValue = parseInt(spotInput.value, 10);
        this.spotToSlideMap[spotValue] = index;
      }
    });
  }

  onSpotChange(event) {
    const spotValue = parseInt(event.target.value, 10); // 1, 2, ou 3
    if (isNaN(spotValue)) return;

    // Utiliser le mapping pour trouver l'index réel du slide
    const slideIndex = this.spotToSlideMap[spotValue];

    if (slideIndex === undefined) return;

    // S'il n'y a pas encore eu de calcul, on le refait proprement
    if (!this.sliderItemsToShow || !this.sliderItemsToShow.length) {
      this.initPages();
    }
    if (!this.sliderItemsToShow[slideIndex]) return;

    // On scroll jusqu'à la position du slide demandé
    const targetLeft = this.sliderItemsToShow[slideIndex].offsetLeft;
    this.setSlidePosition(targetLeft);
  }

  onSlideChanged(event) {
    // currentPage est 1-based dans SliderComponent
    const currentPage = event.detail?.currentPage;
    if (!currentPage) return;

    // Convertir en index 0-based
    const slideIndex = currentPage - 1;

    // Trouver le spot qui correspond à ce slide
    const spotValue = Object.keys(this.spotToSlideMap).find((key) => this.spotToSlideMap[key] === slideIndex);

    if (spotValue) {
      const input = this.querySelector(`.spotlight-point-input[value="${spotValue}"]`);
      if (input) {
        input.checked = true;
      }
    }
  }
}

customElements.define('spotlight-slider-component', SpotlightSliderComponent);
